import { useEffect } from "react"

/**
 * Boot milestones and Web Vitals (performance plan item 1). Each milestone is a `performance.mark("minga:<name>")`
 * (visible in the browser's performance panel) and a sample; Web Vitals (LCP, INP, CLS, TTFB) come from the
 * `web-vitals` package. Samples are batched and sent with `navigator.sendBeacon` to `/api/telemetry/vitals`: when the
 * page is hidden or left, and a while after boot. Never blocks rendering, never throws, and a failed or refused
 * beacon (the endpoint may not exist yet) is dropped. No third-party analytics.
 */

export const VITALS_URL = "/api/telemetry/vitals"
/** Decision 0143: uncaught errors and failed API requests. */
export const EVENTS_URL = "/api/telemetry/events"
/** The server takes at most 20 events per beacon; more in one batch are dropped here (the first ones say enough). */
export const EVENTS_MAX = 20

/** How long after the first sample a batch goes out even if the page stays open. */
export const FLUSH_AFTER_MS = 10_000

/** The boot milestones, in the order they normally happen. */
export type BootMilestone =
  | "main"
  | "session"
  | "workspaces"
  | "shell"
  | "home-data"
  | "project-data"
  | "project-snapshot"
  /** The first agent chat transcript from the server (project load speed, 2026-10-17). */
  | "chat-history"

export interface Sample {
  /** `boot.<milestone>` (ms since navigation start) or a Web Vital name (`LCP`, `INP`, `CLS`, `TTFB`). */
  readonly name: string
  readonly value: number
  /** The route with ids taken out, e.g. `/w/:id/p/:id`. */
  readonly route: string
  readonly rating?: string
  readonly navigationType?: string
}

export interface VitalsBatch {
  readonly build: string
  readonly samples: ReadonlyArray<Sample>
}

declare const __MINGA_BUILD__: string | undefined

/** The build this page runs (set by Vite at build time), or `dev`. */
export const buildId = (): string => (typeof __MINGA_BUILD__ === "string" ? __MINGA_BUILD__ : "dev")

/**
 * A path without ids: any segment with a digit, an underscore or 16+ characters becomes `:id`, so a route says which
 * page it was, never whose workspace or which item.
 */
export const routeTemplate = (pathname: string): string =>
  pathname
    .split("/")
    .map((s) => (s && (/[\d_]/.test(s) || s.length >= 16) ? ":id" : s))
    .join("/")
    .replace(/(.)\/+$/, "$1")

/** Decision 0143: an uncaught error, an unhandled rejection or one a React error boundary caught. No content. */
export interface ClientErrorEvent {
  readonly type: "error"
  readonly name: string
  /** Cut here to 300 characters; the server scrubs secrets, cuts it to 200 and keeps it in its logs only. */
  readonly message: string
  readonly route: string
  readonly source: "error" | "rejection" | "boundary"
  /** Up to five stack frames; the server keeps `<bundle file>:<line>:<col>` of them only. */
  readonly frames?: ReadonlyArray<string>
}

/** Decision 0143: an `/api` request that failed on the server's side or never got an answer. */
export interface ClientRequestEvent {
  readonly type: "request"
  readonly method: string
  readonly route: string
  /** The HTTP status; 0 = timeout or no answer. */
  readonly status: number
  readonly ms: number
  readonly correlationId?: string
  readonly errorCode?: string
}

export type ClientEvent = ClientErrorEvent | ClientRequestEvent

/**
 * A hook for another destination (e.g. web analytics later): every event the beacon sends is also handed to each sink.
 * A sink that throws is ignored.
 */
export type TelemetrySink = (event: ClientEvent) => void
const sinks = new Set<TelemetrySink>()
export const addTelemetrySink = (sink: TelemetrySink): (() => void) => {
  sinks.add(sink)
  return () => sinks.delete(sink)
}

const marked = new Set<string>()
let queue: Sample[] = []
let events: ClientEvent[] = []
let timer: ReturnType<typeof setTimeout> | null = null
let listening = false

const currentRoute = (): string => routeTemplate(globalThis.location?.pathname ?? "/")

const now = (): number => {
  try {
    return Math.round(globalThis.performance?.now() ?? 0)
  } catch {
    return 0
  }
}

/** Send what is queued, if anything. Safe to call any time; never throws. */
export const flush = (): void => {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  const samples = queue
  const sending = events.slice(0, EVENTS_MAX)
  queue = []
  events = []
  try {
    if (typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") return
    if (samples.length > 0) {
      const batch: VitalsBatch = { build: buildId(), samples }
      navigator.sendBeacon(VITALS_URL, new Blob([JSON.stringify(batch)], { type: "application/json" }))
    }
    if (sending.length > 0)
      navigator.sendBeacon(
        EVENTS_URL,
        new Blob([JSON.stringify({ build: buildId(), route: currentRoute(), events: sending })], {
          type: "application/json",
        }),
      )
  } catch {
    // telemetry never breaks the page
  }
}

const listen = () => {
  if (listening || typeof document === "undefined") return
  listening = true
  try {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flush()
    })
    globalThis.addEventListener?.("pagehide", flush)
  } catch {
    // no listeners, the timer still sends
  }
}

/** Queue one sample; it goes out with the next batch. */
export const record = (sample: Omit<Sample, "route"> & { route?: string }): void => {
  queue.push({ ...sample, route: sample.route ?? currentRoute() })
  listen()
  if (!timer) timer = setTimeout(flush, FLUSH_AFTER_MS)
}

/** At most this many events are queued per page between two beacons (an error loop never floods the server). */
export const EVENTS_QUEUE_MAX = 20

/** Queue one event (decision 0143); it goes out with the next beacon. Never throws. */
export const recordEvent = (event: ClientEvent): void => {
  for (const sink of sinks)
    try {
      sink(event)
    } catch {
      // a sink never breaks the page
    }
  if (events.length >= EVENTS_QUEUE_MAX) return
  events.push(event)
  listen()
  if (!timer) timer = setTimeout(flush, FLUSH_AFTER_MS)
}

const SHORT = (s: unknown, max: number): string => (typeof s === "string" ? s.slice(0, max) : "")

/**
 * Browser notices that are not errors of the app: "ResizeObserver loop completed with undelivered notifications" (and
 * "… limit exceeded") arrive as `error` events with no error object whenever an observer's callback resizes what it
 * observes (the virtual chat list measuring its rows). Harmless, and they buried real errors (polish 2026-10-23 §2).
 */
export const IGNORED_ERROR_MESSAGES: ReadonlyArray<RegExp> = [/^ResizeObserver loop/]

const messageOf = (error: unknown): string => {
  if (error instanceof Error) return error.message
  if (typeof error === "string") return error
  const m = (error as { message?: unknown } | null | undefined)?.message
  return typeof m === "string" ? m : String(error)
}

/**
 * What a thrown non-Error is called: the head of its message ("Script error", "Load failed"), up to the first colon,
 * period, parenthesis or line break, so events group by what happened and not by `string`. Its type only when the
 * message says nothing.
 */
export const nonErrorName = (error: unknown): string => {
  const head = (messageOf(error).split(/[\n:.(]/)[0] ?? "").trim().slice(0, 64)
  return head || (error === null ? "null" : typeof error)
}

/** Report an error (decision 0143): its name, a cut message, the route template and the top stack frames. */
export const reportError = (error: unknown, source: ClientErrorEvent["source"]): void => {
  try {
    const text = messageOf(error)
    if (IGNORED_ERROR_MESSAGES.some((re) => re.test(text))) return
    const e = error instanceof Error ? error : null
    const frames = (e?.stack ?? "")
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => /\.m?js(\?[^:\s)]*)?:\d+:\d+/.test(l))
      .slice(0, 5)
      .map((l) => l.slice(0, 200))
    recordEvent({
      type: "error",
      name: SHORT(e ? e.name : nonErrorName(error), 64) || "Error",
      message: SHORT(text, 300),
      route: currentRoute(),
      source,
      ...(frames.length > 0 ? { frames } : {}),
    })
  } catch {
    // never throws
  }
}

/** The API path as a template: ids out (`/api/workspaces/:id/projects/:id`), the query string dropped. */
export const apiRouteTemplate = (path: string): string => routeTemplate(path.split("?")[0] ?? path)

/** Report a failed `/api` request (decision 0143): 5xx, 429 or no answer (status 0); a 4xx is the app's own flow. */
export const reportRequestFailed = (
  e: Omit<ClientRequestEvent, "type" | "route"> & { path: string },
): void => {
  if (!(e.status === 0 || e.status === 429 || e.status >= 500)) return
  if (e.path.startsWith("/api/telemetry")) return
  const { path, ...rest } = e
  recordEvent({ type: "request", ...rest, route: apiRouteTemplate(path), ms: Math.round(e.ms) })
}

let catching = false

/** Start reporting uncaught errors and unhandled rejections (once per page). */
export const startErrorReporting = (): void => {
  if (catching || typeof window === "undefined") return
  catching = true
  try {
    window.addEventListener("error", (ev) => reportError(ev.error ?? ev.message, "error"))
    window.addEventListener("unhandledrejection", (ev) => reportError(ev.reason, "rejection"))
  } catch {
    // no reporting, the page still works
  }
}

/** A milestone reached later than this after navigation is not part of the boot (e.g. a chat opened minutes later). */
export const BOOT_WINDOW_MS = 120_000

/**
 * Project load speed (2026-10-17): boot milestones are rated like LCP (good ≤ 2.5 s, poor > 4 s), so the cloud's log
 * line names the slow ones (`boot.project-snapshot=7000ms(poor)@/w/:id/p/:id`); unrated samples are never logged.
 */
export const bootRating = (ms: number): "good" | "needs-improvement" | "poor" =>
  ms <= 2_500 ? "good" : ms <= 4_000 ? "needs-improvement" : "poor"

/** Mark a boot milestone, once per page load: the first time counts. */
export const markBoot = (name: BootMilestone): void => {
  if (marked.has(name)) return
  marked.add(name)
  const value = now()
  if (value > BOOT_WINDOW_MS) return
  try {
    globalThis.performance?.mark?.(`minga:${name}`)
  } catch {
    // marks are a nicety
  }
  record({ name: `boot.${name}`, value, rating: bootRating(value) })
}

/** Mark a milestone once `ready` turns true (a component's first render with its data). */
export function useBootMark(name: BootMilestone, ready = true): void {
  useEffect(() => {
    if (ready) markBoot(name)
  }, [name, ready])
}

/**
 * Start Web Vitals reporting. The library loads after the page is up (its observers read buffered entries, so LCP and
 * TTFB from before are still seen); a failed load is ignored.
 */
export const startVitals = (): void => {
  if (typeof window === "undefined" || typeof PerformanceObserver === "undefined") return
  const load = () =>
    import("web-vitals")
      .then(({ onCLS, onINP, onLCP, onTTFB }) => {
        const report = (m: { name: string; value: number; rating: string; navigationType: string }) =>
          record({
            name: m.name,
            value: m.name === "CLS" ? Math.round(m.value * 1000) / 1000 : Math.round(m.value),
            rating: m.rating,
            navigationType: m.navigationType,
          })
        onLCP(report)
        onINP(report)
        onCLS(report)
        onTTFB(report)
      })
      .catch(() => {})
  if (document.readyState === "complete") setTimeout(load, 0)
  else window.addEventListener("load", () => setTimeout(load, 0), { once: true })
}

/** Tests only: forget marks and queued samples. */
export const resetTelemetryForTests = (): void => {
  marked.clear()
  queue = []
  events = []
  sinks.clear()
  if (timer) clearTimeout(timer)
  timer = null
}
